import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Loader2,
  RotateCcw,
  ShieldAlert,
  X,
} from "lucide-react";
import {
  fetchRecycleDetails,
  restoreOne,
  type RecycleDetailsResult,
  type RestoreResponse,
} from "@/lib/recycleApi";

const MIN_REASON = 16;

/**
 * Per-row inspect + restore drawer. Pre-flight reads
 * {@code GET /recycle/{storeId}} (which includes the collision check
 * + blocker list). Restore form supports rename-on-restore (the
 * collision blocker explicitly suggests this).
 */
export function RecycleDetailsDrawer({
  storeId,
  onClose,
  onRestored,
}: {
  storeId: string | null;
  onClose: () => void;
  onRestored: (resp: RestoreResponse) => void;
}) {
  const open = !!storeId;
  const qc = useQueryClient();

  const details = useQuery<RecycleDetailsResult>({
    queryKey: ["recycle-details", storeId],
    queryFn: () => fetchRecycleDetails(storeId!),
    enabled: open,
    staleTime: 30_000,
  });

  // Reset form state on open / store-id change.
  const [alternateName, setAlternateName] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAlternateName("");
    setReason("");
    setError(null);
  }, [open, storeId]);

  // When details says collision, prefill the rename input with a
  // sensible suggestion so the operator just confirms / tweaks.
  useEffect(() => {
    if (details.data?.restoreTargetCollides && details.data.object?.name) {
      setAlternateName(details.data.object.name + " (restored)");
    }
  }, [details.data]);

  const restore = useMutation({
    mutationFn: () =>
      restoreOne(storeId!, {
        alternateName: alternateName.trim() || null,
        reason: reason.trim(),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["recycle-list"] });
      qc.invalidateQueries({ queryKey: ["recycle-details", storeId] });
      onRestored(data);
      onClose();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      setError(extractErrorMessage(msg));
    },
  });

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const obj = details.data?.object;
  const blockers = details.data?.blockers ?? [];
  const collides = details.data?.restoreTargetCollides ?? false;
  const isPastRetention = obj?.recoverability === "past_retention";
  const reasonValid = reason.trim().length >= MIN_REASON;
  // Restore is allowed when: past-retention is the only definitive
  // blocker (everything else is recoverable with the right input).
  const restoreDisabled =
    !reasonValid ||
    isPastRetention ||
    !obj?.parentStillExists ||
    (collides && !alternateName.trim()) ||
    restore.isPending;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex"
      role="dialog"
      aria-modal="true"
      aria-label="Recycle bin item details"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex-1 bg-slate-900/40 backdrop-blur-[1px]" />
      <aside className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-2xs font-semibold uppercase tracking-wider text-slate-500">
              Deleted item
            </div>
            <div className="text-sm font-semibold text-slate-900 truncate" title={obj?.name ?? ""}>
              {obj?.name ?? "—"}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {details.isLoading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 size={12} className="animate-spin" /> Loading details…
            </div>
          )}
          {details.error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
              {(details.error as Error).message}
            </p>
          )}
          {obj && (
            <>
              <Section title="Object">
                <Row label="Type" value={obj.objectClass} mono />
                <Row label="Original path" value={obj.displayPath} mono />
                <Row label="Owner" value={obj.ownerUsername} />
                <Row
                  label="Recoverability"
                  value={obj.recoverability}
                  tone={obj.recoverability === "recoverable" ? "ok" : "warn"}
                />
              </Section>
              <Section title="Deletion">
                <Row label="Deleted by" value={obj.deletedByUsername} />
                <Row
                  label="Deleted at"
                  value={obj.deletedAt ? new Date(obj.deletedAt).toLocaleString() : "—"}
                />
              </Section>
              <Section title="Restore target">
                <Row label="Target path" value={details.data?.restoreTargetPath} mono />
                <Row
                  label="Parent still exists"
                  value={obj.parentStillExists ? "Yes" : "No"}
                  tone={obj.parentStillExists ? "ok" : "warn"}
                />
                <Row
                  label="Name collision"
                  value={collides ? "Yes — pass alternateName" : "No"}
                  tone={collides ? "warn" : "ok"}
                />
              </Section>

              {blockers.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                    <ShieldAlert size={12} /> Blockers
                  </div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-2xs text-amber-900">
                    {blockers.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* Restore form */}
        <div className="border-t border-slate-200 px-4 py-3 space-y-2.5">
          <div>
            <label className="text-2xs font-medium uppercase tracking-wider text-slate-500">
              Alternate name (optional, required on collision)
            </label>
            <input
              type="text"
              value={alternateName}
              onChange={(e) => setAlternateName(e.target.value)}
              placeholder={obj?.name ?? "Leave blank to keep original name"}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              maxLength={256}
            />
          </div>
          <div>
            <label className="text-2xs font-medium uppercase tracking-wider text-slate-500">
              Reason ({MIN_REASON}+ chars · audit-logged)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Accidental delete by jsmith per ticket OPS-3421"
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              maxLength={1000}
            />
            {!reasonValid && reason.length > 0 && (
              <p className="mt-0.5 text-[10px] text-rose-600">
                {MIN_REASON - reason.trim().length} more characters needed.
              </p>
            )}
          </div>
          {error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 inline-flex items-start gap-1.5">
              <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </p>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={restoreDisabled}
              onClick={() => {
                setError(null);
                restore.mutate();
              }}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {restore.isPending && <Loader2 size={11} className="animate-spin" />}
              <RotateCcw size={11} />
              Restore
            </button>
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </h3>
      <dl className="space-y-1 rounded-md border border-slate-100 bg-slate-50/60 p-2.5">{children}</dl>
    </section>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  tone?: "ok" | "warn";
}) {
  const toneClass =
    tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-800";
  return (
    <div className="flex items-baseline justify-between gap-2 text-2xs">
      <dt className="shrink-0 font-medium text-slate-500">{label}</dt>
      <dd
        className={`min-w-0 truncate text-right ${toneClass} ${mono ? "font-mono" : ""}`}
        title={value ?? undefined}
      >
        {value && value.length > 0 ? value : "—"}
      </dd>
    </div>
  );
}

/** Best-effort error extraction from axios's error.response.data shape. */
function extractErrorMessage(raw: string): string {
  // Axios stringifies as "Request failed with status code 409"; the
  // controller's error body has { error, message }. Try to surface the
  // controller's message when present.
  if (raw.includes("403")) return "Admin role required to restore.";
  return raw;
}
